/** @jsxImportSource @emotion/react */
/**
 * Компонент индикатора состояния сети и синхронизации
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Space, Tooltip, Tag, Popover, Progress, Button } from 'antd';
import {
  WifiOutlined,
  CloudUploadOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { photoSyncService, type SyncEvent } from '../../services/photoSyncService';
import { getStorageStats } from '../../utils/imageStorage';
import { commonStyles, colors } from '../../styles/commonStyles';
import { css } from '@emotion/react';

const styles = {
  popoverContent: css({ minWidth: 200 }),
  iconOnline: css({ color: '#52c41a' }),
  iconOffline: css({ color: '#ff4d4f' }),
  iconSync: css({ color: colors.primary }),
  iconPending: css({ color: '#faad14' }),
};

interface NetworkStatusProps {
  style?: React.CSSProperties;
}

const NetworkStatus: React.FC<NetworkStatusProps> = ({ style }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0 });
  const [stats, setStats] = useState({
    pending: 0,
    failed: 0,
    total: 0,
    totalSize: 0,
  });

  /**
   * Обновление статистики хранилища
   */
  const updateStats = useCallback(async () => {
    const storageStats = await getStorageStats();
    setStats({
      pending: storageStats.pending,
      failed: storageStats.failed,
      total: storageStats.total,
      totalSize: storageStats.totalSize,
    });
  }, []);

  /**
   * Обработка событий синхронизации
   */
  const handleSyncEvent = useCallback((event: SyncEvent) => {
    switch (event.type) {
      case 'started':
        setIsSyncing(true);
        break;
      case 'progress':
        setSyncProgress({ current: event.current || 0, total: event.total || 0 });
        break;
      case 'completed':
      case 'failed':
        setIsSyncing(false);
        setSyncProgress({ current: 0, total: 0 });
        updateStats();
        break;
      case 'photo-uploaded':
        updateStats();
        break;
    }
  }, [updateStats]);

  /**
   * Подписка на события
   */
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const unsubscribe = photoSyncService.subscribe(handleSyncEvent);

    // Начальная загрузка статистики
    updateStats();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsubscribe();
    };
  }, [handleSyncEvent, updateStats]);

  const pendingCount = stats.pending + stats.failed;
  const syncPercentage =
    syncProgress.total > 0 ? Math.round((syncProgress.current / syncProgress.total) * 100) : 0;

  const statusIcon = isOnline ? (
    <WifiOutlined css={styles.iconOnline} />
  ) : (
    <ExclamationCircleOutlined css={styles.iconOffline} />
  );

  const content = (
    <Space direction="vertical" size={8} css={styles.popoverContent}>
      <div>
        <strong>Статус:</strong>{' '}
        {isOnline ? (
          <Tag color="success" icon={<WifiOutlined />}>
            Онлайн
          </Tag>
        ) : (
          <Tag color="error" icon={<ExclamationCircleOutlined />}>
            Офлайн
          </Tag>
        )}
      </div>

      {stats.total > 0 && (
        <>
          <div>
            <strong>Локальных фото:</strong> {stats.total}
          </div>
          <div>
            <strong>Ожидают загрузки:</strong>{' '}
            <Tag color={pendingCount > 0 ? 'warning' : 'default'}>{pendingCount}</Tag>
          </div>
          {stats.failed > 0 && (
            <div>
              <strong>Ошибок:</strong> <Tag color="error">{stats.failed}</Tag>
            </div>
          )}
          <div>
            <strong>Размер:</strong> {(stats.totalSize / 1024 / 1024).toFixed(2)} MB
          </div>
        </>
      )}

      {isSyncing && (
        <div>
          <Progress percent={syncPercentage} size="small" />
          <div css={[commonStyles.smallText, commonStyles.secondaryText]}>
            {syncProgress.current} / {syncProgress.total}
          </div>
        </div>
      )}

      {pendingCount > 0 && isOnline && !isSyncing && (
        <Button
          type="primary"
          size="small"
          icon={<CloudUploadOutlined />}
          onClick={() => photoSyncService.sync(true)}
          block
        >
          Синхронизировать
        </Button>
      )}
    </Space>
  );

  return (
    <Popover content={content} title="Состояние сети" trigger="hover">
      <div css={commonStyles.pointer} style={style}>
        {isSyncing ? (
          <Badge dot status="processing">
            <Space size={4}>
              <SyncOutlined spin css={styles.iconSync} />
              <span css={commonStyles.smallText}>Синхронизация...</span>
            </Space>
          </Badge>
        ) : pendingCount > 0 ? (
          <Badge count={pendingCount} offset={[0, 0]} size="small">
            <Space size={4}>
              <CloudUploadOutlined css={styles.iconPending} />
              <span css={commonStyles.smallText}>
                {isOnline ? 'Ожидает загрузки' : 'Офлайн'}
              </span>
            </Space>
          </Badge>
        ) : (
          <Tooltip title={isOnline ? 'Онлайн' : 'Офлайн'}>
            <Space size={4}>
              {statusIcon}
              <span css={commonStyles.smallText}>{isOnline ? 'Онлайн' : 'Оффлайн'}</span>
            </Space>
          </Tooltip>
        )}
      </div>
    </Popover>
  );
};

export default NetworkStatus;
